import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Field, FormikProvider, useFormik } from 'formik'

import CustomInput from 'ui/CustomInput'
import CustomToast from 'ui/CustomToast'
import client from 'api/client'
import { handleRequestError } from 'api/handleRequestError'
import { useAuthStore } from 'features/auth/store/authStore'
import { paymentContactValidationSchema } from 'utils/validationSchemas/formSchemas'
import { Icons } from 'components/Icons'

const { RiAlertFill } = Icons

const PaymentContactModal = ({ isOpen }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const paymentContact = useAuthStore((state) => state.paymentContact)

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: paymentContactValidationSchema,
    initialValues: {
      first_name: paymentContact?.first_name || '',
      last_name: paymentContact?.last_name || '',
      title: paymentContact?.title || '',
      phone: paymentContact?.phone || '',
      email: paymentContact?.email || '',
    },
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true)
        const response = await client.put('/auth/user/payment-contact', values)
        if (response.data?.success) {
          useAuthStore
            .getState()
            .setPaymentContact(response.data.payment_contact)
          useAuthStore.getState().setIsPaymentContactCompleted(true)
          CustomToast({
            message: 'Payment contact saved successfully',
            type: 'success',
          })
        }
      } catch (error) {
        handleRequestError(error)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  if (!isOpen) return null

  return (
    <div className='fixed inset-0 z-[9999] overflow-y-auto flex items-center justify-center p-4 bg-gray-50 pointer-events-auto'>
      <div className='relative w-full max-w-lg p-6 md:p-8 bg-white shadow-2xl rounded-2xl animate-fadeIn'>
        <div className='flex flex-col items-center mb-6 text-center'>
          <div className='flex items-center justify-center w-14 h-14 mb-3 rounded-full bg-primary/10 text-primary'>
            <RiAlertFill className='text-3xl' />
          </div>
          <h3 className='text-2xl font-bold text-gray-900'>
            Payment Contact Required
          </h3>
          <p className='mt-2 text-sm text-gray-600 leading-relaxed'>
            To continue using Revvit wholesale services, please provide your
            dealership&apos;s designated payment and settlement contact details.
            This information will appear on Bills of Sale for direct
            dealer-to-dealer transactions.
          </p>
        </div>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <Field
                name='first_name'
                label='First Name'
                placeholder='Enter first name'
                component={CustomInput}
                className='form-field'
                required
              />
              <Field
                name='last_name'
                label='Last Name'
                placeholder='Enter last name'
                component={CustomInput}
                className='form-field'
                required
              />
            </div>

            <Field
              name='title'
              label='Job Title / Role'
              placeholder='e.g., Finance Manager / Controller'
              component={CustomInput}
              className='form-field'
              required
            />

            <Field
              name='phone'
              label='Direct Phone Number'
              placeholder='e.g., (555) 000-0000'
              component={CustomInput}
              className='form-field'
              required
            />

            <Field
              name='email'
              type='email'
              label='Accounting / Settlement Email'
              placeholder='e.g., accounting@dealership.com'
              component={CustomInput}
              className='form-field'
              required
            />

            <div className='pt-4'>
              <button
                type='submit'
                disabled={isSubmitting}
                className='w-full py-3 text-base font-semibold text-white transition-all rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50'
              >
                {isSubmitting ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
          </form>
        </FormikProvider>
      </div>
    </div>
  )
}

PaymentContactModal.propTypes = {
  isOpen: PropTypes.bool,
}

PaymentContactModal.defaultProps = {
  isOpen: true,
}

export default PaymentContactModal
